// ============================================================================
// reactionPictures.ts — SSoT картинок-реакций (ветка «Картинки» конфигурации).
// Загружает библиотеку 'reactions' через PictureService и кэширует содержимое
// файлов (SVG) как dataURL. Код реакции = metadata_pictures.code
// ('reaction_like', ...); хранится в chat_messages.reactions[].emoji как есть.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { eventLog } from '@/lib/eventLog';
import { fileBase64 } from '@/lib/fileBytes';

const log = createLogger('reactionPictures');

const cache = new Map<string, string>(); // code → dataURL
const labels = new Map<string, string>(); // code → синоним («Like»…)
const chars = new Map<string, string>(); // code → эмодзи-символ (name) для вставки в текст
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

/** Канонический порядок реакций (= SystemReactions на сервере); коды — БЕЗ изменений */
export const REACTION_PICTURE_ORDER = [
  'reaction_like', 'reaction_heart', 'reaction_laugh', 'reaction_sad', 'reaction_shock', 'reaction_pray',
  'reaction_fire', 'reaction_clap', 'reaction_thumbs_down', 'reaction_eyes', 'reaction_rocket', 'reaction_100',
  'reaction_thinking', 'reaction_wink', 'reaction_party', 'reaction_cool',
];

/** ЯВНЫЙ fallback SSoT-реестра: детерминированный встроенный набор реакций.
 *  Срабатывает, когда библиотека «Картинки» недоступна (ошибка/офлайн/пустая
 *  база): панель выбора и чипы сообщений обязаны иметь содержимое всегда.
 *  Коды и символы ЗЕРКАЛЯТ SystemReactions сервера — идентификатор reaction_*
 *  один и тот же в обоих режимах (совместимость сохранённых реакций). */
const REACTION_FALLBACK: Record<string, { char: string; label: string }> = {
  reaction_like: { char: '\u{1F44D}', label: 'Нравится' },
  reaction_heart: { char: '❤', label: 'Сердце' },
  reaction_laugh: { char: '\u{1F602}', label: 'Смех' },
  reaction_sad: { char: '\u{1F622}', label: 'Грусть' },
  reaction_shock: { char: '\u{1F631}', label: 'Ужас' },
  reaction_pray: { char: '\u{1F64F}', label: 'Молитва' },
  reaction_fire: { char: '\u{1F525}', label: 'Огонь' },
  reaction_clap: { char: '\u{1F44F}', label: 'Аплодисменты' },
  reaction_thumbs_down: { char: '\u{1F44E}', label: 'Не нравится' },
  reaction_eyes: { char: '\u{1F440}', label: 'Глаза' },
  reaction_rocket: { char: '\u{1F680}', label: 'Ракета' },
  reaction_100: { char: '\u{1F4AF}', label: 'Сто' },
  reaction_thinking: { char: '\u{1F914}', label: 'Задумался' },
  reaction_wink: { char: '\u{1F609}', label: 'Подмигивает' },
  reaction_party: { char: '\u{1F389}', label: 'Праздник' },
  reaction_cool: { char: '\u{1F60E}', label: 'Круто' },
};

/** URL картинки реакции (если загружена) */
export function reactionUrl(code: string): string | undefined {
  return cache.get(code);
}

/** Человекочитаемая подпись реакции (из Картинок; иначе — встроенная, иначе код) */
export function reactionLabel(code: string): string {
  return labels.get(code) || REACTION_FALLBACK[code]?.label || code;
}

/** Эмодзи-символ реакции: из «Картинок» (name), иначе встроенный fallback */
export function reactionChar(code: string): string {
  return chars.get(code) || REACTION_FALLBACK[code]?.char || '';
}

/** Что показать для реакции: SVG из «Картинок» (побеждает) либо встроенный символ */
export interface ReactionDisplay {
  url: string | undefined;
  char: string;
  label: string;
}

export function reactionDisplay(code: string): ReactionDisplay {
  return { url: cache.get(code), char: reactionChar(code), label: reactionLabel(code) };
}

/** Известный код реакции: реестр «Картинок» либо встроенный fallback */
export function isKnownReaction(code: string): boolean {
  return cache.has(code) || code in REACTION_FALLBACK;
}

/** Все известные коды реакций (после загрузки библиотеки) */
export function knownReactionCodes(): string[] {
  return Array.from(cache.keys());
}

/** Коды для панели выбора: канонический порядок + расширения из «Картинок» */
export function reactionCodes(): string[] {
  const extra = Array.from(cache.keys()).filter((c) => !REACTION_PICTURE_ORDER.includes(c));
  return [...REACTION_PICTURE_ORDER, ...extra];
}

/** Подписка на готовность/обновление кэша */
export function subscribeReactionPictures(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Однократная загрузка библиотеки реакций (идемпотентно, без таймеров).
 *  Итог пишется в Журнал регистрации (категория ui) — наблюдаемость SSoT. */
export function ensureReactionPictures(): Promise<void> {
  if (!loading) {
    loading = (async () => {
      const r: any = await getSDK().picture.listPictures({ libraryCode: 'reactions' });
      const items = (r?.items || []) as any[];
      for (const p of items) {
        labels.set(p.code, p.synonym || p.name || p.code);
        // name хранит эмодзи-символ (системные) — для вставки в текст
        if (p.name && String(p.name).length <= 8) chars.set(p.code, String(p.name));
      }
      const targets = items.filter((p) => p.fileId && !cache.has(p.code));
      const failed: { code: string; error: string }[] = [];
      // ПОСЛЕДОВАТЕЛЬНО: шторм параллельных unary в WebView2 не нужен
      for (const p of targets) {
        try {
          const f: any = await getSDK().files.downloadFile({ id: p.fileId });
          // bytes поле: grpc-web → Uint8Array, connect-JSON → base64-строка
          const d = fileBase64(f?.data);
          if (d) cache.set(p.code, `data:${f?.fileType || 'image/svg+xml'};base64,${d}`);
          else failed.push({ code: p.code, error: 'пустой data' });
        } catch (e: any) {
          failed.push({ code: p.code, error: String(e?.message || e) });
        }
      }
      for (const l of listeners) l();
      const withFile = items.filter((p) => p.fileId).length;
      if (items.length === 0) {
        eventLog.warning('ui', 'Реакции: библиотека «Картинки» пуста — используется встроенный набор');
      } else if (failed.length > 0) {
        loading = null; // неудачные дозагрузятся при следующем монтировании бабла
        eventLog.warning('ui', `Реакции: не загрузились ${failed.length}/${withFile}`, { details: JSON.stringify(failed.slice(0, 5)) });
      } else {
        eventLog.info('ui', 'Реакции загружены из Картинок', { details: `count=${cache.size}/${withFile}` });
      }
    })().catch((e: any) => {
      loading = null;
      log.warn(`ensureReactionPictures failed: ${String(e?.message || e)}`);
      throw e;
    });
  }
  return loading;
}
